import React, { PureComponent } from 'react'
import cx from 'classnames'
import { Message } from 'sb-react-intl'

import { Box } from 'components/_new/layout'

import TabbedContentBaseTab from './TabbedContentBaseTab'


export type Tab = {
  id: string | number
  to?: string
  dataTestId?: string
  content?: string | React.ReactNode
} & OnlyOne<{
  title: ReactIntlMessage | string
  titleContent: React.ReactNode // to replace title
}>

type StylesObject = {
  tabsContainer?: string
  tabs?: string
  tabContainer?: string
  tab?: string
  active?: string
  content?: string
}

export type TabbedContentBaseProps = {
  styles: StylesObject
  tabs: Tab[]
  defaultActiveTabId?: string | number
  activeTabId?: string | number
  className?: string
  tabClassName?: string,
  tabsClassName?: string
  tabContainerClassName?: string
  tabsContainerClassName?: string
  activeTabClassName?: string
  contentClassName?: string
  children?: (activeTab: Tab, activeTabIndex: number) => any
  passFullDataToCallback?: boolean
  onTabClick?: ((id: string | number) => void) | ((tab: Tab) => void)
}

type TabbedContentBaseState = {
  activeTabIndex: number
}

class TabbedContentBase extends PureComponent<TabbedContentBaseProps, TabbedContentBaseState> {

  constructor(props) {
    super(props)

    const { defaultActiveTabId, tabs } = props

    let activeTabIndex = 0

    if (defaultActiveTabId) {
      activeTabIndex = tabs.map(({ id }) => id).indexOf(defaultActiveTabId)
    }

    this.state = {
      activeTabIndex,
    }
  }

  handleTabClick = (index) => {
    const { onTabClick, tabs, passFullDataToCallback } = this.props

    const { activeTabIndex } = this.state

    if (activeTabIndex !== index && tabs[index]) {
      this.setState({
        activeTabIndex: index,
      })

      if (onTabClick) {
        const data = passFullDataToCallback ? tabs[index] : (tabs[index].id || index)

        onTabClick(data)
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<TabbedContentBaseProps>): void {
    const { activeTabIndex } = this.state
    const { activeTabId, tabs } = this.props
    const { activeTabId: prevActiveTabId } = prevProps

    if (activeTabId && activeTabId !== prevActiveTabId) {
      const nextActiveTabIndex = tabs.map(({ id }) => id).indexOf(activeTabId)

      if (nextActiveTabIndex !== activeTabIndex) {
        this.setState({
          activeTabIndex: nextActiveTabIndex,
        })
      }
    }
  }

  render() {
    const { activeTabIndex } = this.state
    const {
      children, className, tabClassName, tabsClassName, tabsContainerClassName, tabContainerClassName,
      tabs, styles, activeTabClassName, contentClassName,
    } = this.props

    const areTabsExist = Boolean(tabs && tabs.length)

    if (!areTabsExist) {
      return null
    }

    const activeTab = tabs[activeTabIndex] || {} as Tab
    const { content } = activeTab

    return (
      <div className={className}>
        <div className={cx(styles.tabsContainer, tabsContainerClassName)}>
          <div className={cx(styles.tabs, tabsClassName)}>
            {
              tabs.map(({ title, to, titleContent, dataTestId }, index) => {
                const tabElementClassName = cx(
                  styles.tab,
                  tabClassName,
                  {
                    [styles.active]: index === activeTabIndex,
                    [activeTabClassName]: activeTabClassName && index === activeTabIndex,
                  }
                )

                const children = titleContent ? (
                  <div className={tabElementClassName}>{titleContent}</div>
                ) : (
                  // @ts-ignore
                  <Message className={tabElementClassName} tag="div" text={title} />
                )

                return (
                  <TabbedContentBaseTab
                    key={index}
                    className={cx(styles.tabContainer, tabContainerClassName)}
                    to={to}
                    index={index}
                    dataTestId={dataTestId}
                    onClick={this.handleTabClick}
                  >
                    {children}
                  </TabbedContentBaseTab>
                )
              })
            }
          </div>
        </div>
        {/* key={activeTabIndex} required to rerender content fully when active tab changes! Check SCNT-4929 */}
        <Box key={activeTabIndex} className={cx(styles.content, contentClassName)} hideEmpty>
          {
            typeof children === 'function' ? (
              children(activeTab, activeTabIndex)
            ) : (
              typeof content === 'string' ? <span dangerouslySetInnerHTML={{ __html: content }} /> : content
            )
          }
        </Box>
      </div>
    )
  }
}


export default TabbedContentBase
